/**
 * useCurrentWorkspace — flag-gated reader of the WorkspaceProvider context.
 *
 * Spec: specs/remediation-plan.md §2.3 (Fix B, the keystone), specs/prd.md §5
 * (FR-004, FR-009). Implemented by T311 against the red-phase tests authored
 * by T309 in `useCurrentWorkspace.test.tsx`.
 *
 * Behaviour contract
 * ──────────────────
 *   - When the `workspace_context_v1` feature flag is OFF (`false`) or still
 *     loading (`undefined`), the hook returns permanent `{ status: "loading" }`.
 *     This preserves the previous stub's contract and gives operators a one-
 *     toggle rollback path that does not require reverting code.
 *
 *   - When the flag is ON (`true`), the hook reads the WorkspaceProvider
 *     context and maps each of its five discriminated variants onto the
 *     `UseCurrentWorkspaceResult` shape. The mapping uses an exhaustive
 *     `switch` whose default arm assigns the discriminated value to a
 *     `const _exhaustive: never` — a future variant added to the provider
 *     without a matching `case` here becomes a TypeScript compile error.
 *
 * Rules of Hooks
 * ──────────────
 *   Both `useFeatureFlag` and `useWorkspaceContext` are called UNCONDITIONALLY
 *   at the top of the function, before any early return. This keeps the hook
 *   call order stable across renders regardless of the flag value.
 *
 * Shape additions
 * ───────────────
 *   `WorkspaceData` now carries `id` in addition to `name` and `slug`. Every
 *   downstream composite that scopes queries by workspace requires the
 *   workspace identifier (FR-004) — surfacing it inside `data` is the natural
 *   place for it given the existing public union (`status` + optional `data`).
 *   Existing consumers that read only `data?.name` / `data?.slug` (the
 *   SideRail switcher trigger, for example) remain source-compatible.
 */

import { useFeatureFlag } from "./useFeatureFlag";
import { useWorkspaceContext } from "../../components/providers/WorkspaceProvider";

/* ------------------------------------------------------------------ */
/*  Public types                                                       */
/* ------------------------------------------------------------------ */

/**
 * Workspace identity surfaced to consumers when the hook is in the `ready`
 * state. `id` is the canonical workspace identifier composites need to scope
 * their queries; `slug` is the URL-safe handle; `name` is the human label.
 */
export interface WorkspaceData {
  id: string;
  name: string;
  slug: string;
}

/**
 * Five-variant result surface mirroring the standard query-state shape used
 * across the dashboard's named hooks. The discriminant is `status`; `data` is
 * populated only when `status === "ready"`. Callers must guard reads on
 * `status === "ready"` before touching `data`.
 */
export interface UseCurrentWorkspaceResult {
  status: "loading" | "ready" | "empty" | "error" | "forbidden";
  data?: WorkspaceData;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Reads the active workspace, gated by the `workspace_context_v1` feature
 * flag. See module docstring for the full behaviour contract.
 */
export function useCurrentWorkspace(): UseCurrentWorkspaceResult {
  // (1) Unconditional hook calls — Rules-of-Hooks compliance. Both calls run
  //     on every render regardless of the flag value, so React's hook-order
  //     invariant cannot drift between flag-off and flag-on rollouts.
  const flag = useFeatureFlag("workspace_context_v1");
  const ctx = useWorkspaceContext();

  // (2) Flag-gated rollback path.
  //     `useFeatureFlag` returns `boolean | undefined`:
  //       - `undefined` while the Convex query is loading
  //       - `false` when the flag is explicitly off
  //       - `true` when the flag is explicitly on
  //     The first two cases collapse onto permanent loading so a freshly
  //     mounted app does not flash a partial workspace before the flag value
  //     arrives, and a flag-off rollback yields the exact pre-Fix-B contract.
  if (flag !== true) {
    return { status: "loading" };
  }

  // (3) Flag-on path: map the provider's five-variant context onto our
  //     five-variant result. The exhaustive `switch` plus the `never`-typed
  //     default-arm assignment turns any future provider variant added
  //     without a matching `case` here into a compile-time error.
  switch (ctx.status) {
    case "loading":
      return { status: "loading" };

    case "ready":
      return {
        status: "ready",
        data: {
          id: ctx.workspaceId,
          name: ctx.name,
          slug: ctx.slug,
        },
      };

    case "empty":
      return { status: "empty" };

    case "error":
      return { status: "error" };

    case "forbidden":
      return { status: "forbidden" };

    default: {
      // Compile-time exhaustiveness check: if `WorkspaceContextValue` grows a
      // new variant, `ctx` will no longer be `never` here and TypeScript will
      // flag the assignment below as an error — exactly the desired behaviour.
      const _exhaustive: never = ctx;
      // Runtime fallback: keep callers in a safe `loading` state if a future
      // variant somehow slips through (defence-in-depth — the assignment
      // above is the primary guarantee).
      void _exhaustive;
      return { status: "loading" };
    }
  }
}
